import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  FiLifeBuoy,
  FiMail,
  FiSend,
  FiLock,
  FiRefreshCw,
  FiUser,
  FiClock,
  FiGitMerge,
  FiTag,
  FiExternalLink,
  FiAlertCircle,
  FiPlus,
  FiArrowDownLeft,
  FiArrowUpRight,
  FiPaperclip,
  FiX,
} from 'react-icons/fi';
import { formatDistanceToNow, parseISO } from 'date-fns';
import api from '../config/api';
import { queryKeys } from '../config/queryKeys';
import { usePermissions } from '../context/PermissionsContext';
import { FadeIn } from '../components/motion';
import ListSearchBar from '../components/ListSearchBar';
import Modal from '../components/Modal';
import { appAlert, appConfirm } from '../utils/appDialog';
import SupportSlaClock from '../components/SupportSlaClock';
import './Support.css';
import ListPager from '../components/ListPager';
import { DEFAULT_LIST_LIMIT } from '../utils/listPagination';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Open + pending' },
  { value: 'open', label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: '', label: 'All' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'Any priority' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

function formatWhen(value) {
  if (!value) return '—';
  try {
    return formatDistanceToNow(parseISO(value), { addSuffix: true });
  } catch {
    return value;
  }
}

const STATUS_META = {
  open: { label: 'Open', hint: 'Needs agent attention' },
  pending: { label: 'Pending', hint: 'Waiting on customer or third party' },
  resolved: { label: 'Resolved', hint: 'Fixed — may still reopen' },
  closed: { label: 'Closed', hint: 'Done / archived' },
};

const PRIORITY_META = {
  low: { label: 'Low', hint: 'Can wait' },
  normal: { label: 'Normal', hint: 'Standard priority' },
  high: { label: 'High', hint: 'Prioritize soon' },
  urgent: { label: 'Urgent', hint: 'Handle immediately' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status || '—', hint: '' };
  return (
    <span
      className={`support-badge support-badge--${status || 'unknown'}`}
      title={meta.hint}
      data-status={status || ''}
    >
      <span className="support-badge-dot" aria-hidden />
      {meta.label}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const meta = PRIORITY_META[priority] || { label: priority || '—', hint: '' };
  return (
    <span
      className={`support-priority support-priority--${priority || 'unknown'}`}
      title={meta.hint}
      data-priority={priority || ''}
    >
      <span className="support-priority-mark" aria-hidden />
      {meta.label}
    </span>
  );
}

function commentKind(message) {
  if (message.is_internal || message.direction === 'internal') return 'note';
  if (message.direction === 'outbound') return 'reply';
  return 'customer';
}

function commentLabel(message) {
  const kind = commentKind(message);
  if (kind === 'note') return 'Private note · team only';
  if (kind === 'reply') return 'Outgoing · emailed to customer';
  return 'Incoming · from customer';
}

function DirectionChip({ kind }) {
  if (kind === 'note') {
    return (
      <span className="support-dir support-dir--note">
        <FiLock aria-hidden /> Private
      </span>
    );
  }
  if (kind === 'reply') {
    return (
      <span className="support-dir support-dir--out">
        <FiArrowUpRight aria-hidden /> Outgoing
      </span>
    );
  }
  return (
    <span className="support-dir support-dir--in">
      <FiArrowDownLeft aria-hidden /> Incoming
    </span>
  );
}

const Support = () => {
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('support_manage');
  const canCreate = hasPermission('support_create') || canManage;

  const [status, setStatus] = useState('active');
  const [priority, setPriority] = useState('');
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(routeId || null);
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [noteMode, setNoteMode] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeSourceId, setMergeSourceId] = useState(null);
  const [mergeNote, setMergeNote] = useState('');
  const [tagDraft, setTagDraft] = useState('');
  const threadEndRef = useRef(null);

  useEffect(() => {
    if (routeId) setSelectedId(routeId);
  }, [routeId]);

  useEffect(() => {
    setPage(1);
  }, [status, priority, search]);

  const filters = useMemo(
    () => ({ status, priority, search, page, limit: DEFAULT_LIST_LIMIT }),
    [status, priority, search, page]
  );

  const { data: statsPayload } = useQuery(queryKeys.supportStats, async () => {
    const { data } = await api.get('/support/stats');
    return data;
  });
  const statsData = statsPayload?.stats;
  const supportInbox = statsPayload?.support_inbox || 'support@gobunnyy.com';

  const { data: agentsData } = useQuery(
    queryKeys.supportAgents,
    async () => {
      const { data } = await api.get('/support/agents');
      return data.agents || [];
    },
    { enabled: canManage }
  );

  const {
    data: listData,
    isLoading: listLoading,
    isFetching: listFetching,
    refetch: refetchList,
  } = useQuery(
    queryKeys.supportTickets(filters),
    async () => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.priority) params.set('priority', filters.priority);
      if (filters.search) params.set('search', filters.search);
      params.set('page', String(filters.page));
      params.set('limit', String(filters.limit));
      const { data } = await api.get(`/support/tickets?${params}`);
      return data;
    },
    { keepPreviousData: true, refetchInterval: 30000 }
  );

  const tickets = listData?.tickets || [];

  const {
    data: detailData,
    isLoading: detailLoading,
    refetch: refetchDetail,
  } = useQuery(
    queryKeys.supportTicket(selectedId),
    async () => {
      const { data } = await api.get(`/support/tickets/${encodeURIComponent(selectedId)}`);
      return data;
    },
    { enabled: Boolean(selectedId), refetchInterval: 20000 }
  );

  const ticket = detailData?.ticket;
  const messages = detailData?.messages || [];
  const related = detailData?.related || [];
  const redirectTo = detailData?.redirect_to;

  const { data: mergeCandidatesData, isFetching: mergeCandidatesLoading } = useQuery(
    ['support', 'merge-candidates', ticket?.id, mergeSearch],
    async () => {
      const params = new URLSearchParams();
      if (mergeSearch) params.set('search', mergeSearch);
      const { data } = await api.get(
        `/support/tickets/${ticket.id}/merge-candidates?${params}`
      );
      return data.candidates || [];
    },
    { enabled: Boolean(mergeOpen && ticket?.id && canManage) }
  );

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, selectedId]);

  useEffect(() => {
    setTagDraft('');
    setReplyText('');
    setReplyFiles([]);
    setNoteMode(false);
  }, [selectedId]);

  const invalidateAll = () => {
    queryClient.invalidateQueries(['support']);
  };

  const patchMutation = useMutation(
    async (patch) => {
      const { data } = await api.patch(`/support/tickets/${ticket.id}`, patch);
      return data;
    },
    {
      onSuccess: () => {
        invalidateAll();
        refetchDetail();
      },
      onError: (err) => {
        appAlert(err.response?.data?.error || err.message || 'Update failed');
      },
    }
  );

  const addReplyFiles = (list) => {
    const next = [...replyFiles];
    for (const file of list) {
      if (next.length >= 5) break;
      if (file.size > 5 * 1024 * 1024) {
        appAlert(`${file.name} exceeds the 5 MB limit`);
        continue;
      }
      next.push(file);
    }
    if (list.length && next.length >= 5 && replyFiles.length + list.length > 5) {
      appAlert('You can attach up to 5 files');
    }
    setReplyFiles(next);
  };

  const downloadAttachment = async (messageId, file) => {
    try {
      const response = await api.get(
        `/support/tickets/${encodeURIComponent(ticket.id)}/messages/${messageId}/attachments/${file.id}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.file_name || 'attachment';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      appAlert(err.response?.data?.error || err.message || 'Could not download the file');
    }
  };

  const sendMutation = useMutation(
    async () => {
      const text = replyText.trim();
      if (!text && replyFiles.length === 0) throw new Error('Comment is required');
      const form = new FormData();
      form.append('body', text);
      form.append('type', noteMode ? 'note' : 'reply');
      replyFiles.forEach((file) => form.append('attachments', file));
      const { data } = await api.post(`/support/tickets/${ticket.id}/comments`, form);
      return data;
    },
    {
      onSuccess: (data) => {
        setReplyText('');
        setReplyFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
        invalidateAll();
        refetchDetail();
        if (!noteMode && data?.email?.skipped) {
          appAlert(
            `Comment saved, but email was skipped (${data.email.reason || 'disabled'}). Configure SMTP in Settings → Email.`
          );
        }
      },
      onError: (err) => {
        appAlert(err.response?.data?.error || err.message || 'Send failed');
      },
    }
  );

  const mergeMutation = useMutation(
    async () => {
      if (!mergeSourceId) throw new Error('Select a ticket to merge');
      const { data } = await api.post(`/support/tickets/${ticket.id}/merge`, {
        source_ticket_id: mergeSourceId,
        note: mergeNote.trim() || undefined,
      });
      return data;
    },
    {
      onSuccess: () => {
        setMergeOpen(false);
        setMergeSourceId(null);
        setMergeNote('');
        setMergeSearch('');
        invalidateAll();
        refetchDetail();
        appAlert('Tickets merged. Comments from the duplicate are now on this ticket.');
      },
      onError: (err) => {
        appAlert(err.response?.data?.error || err.message || 'Merge failed');
      },
    }
  );

  const selectTicket = (t) => {
    setSelectedId(t.id);
    navigate(`/support/${t.id}`, { replace: true });
  };

  const addTag = () => {
    const tag = tagDraft.trim().toLowerCase();
    if (!tag || !ticket) return;
    const next = [...new Set([...(ticket.tags || []), tag])];
    setTagDraft('');
    patchMutation.mutate({ tags: next });
  };

  const removeTag = (tag) => {
    const next = (ticket.tags || []).filter((t) => t !== tag);
    patchMutation.mutate({ tags: next });
  };

  const openMerge = async () => {
    const ok = await appConfirm(
      'Merge another ticket into this one? Comments move here; the duplicate closes. Customer replies to either ticket number stay on this ticket.'
    );
    if (!ok) return;
    setMergeOpen(true);
  };

  return (
    <div className="support-page list-page">
      <FadeIn className="page-header">
        <div className="page-header-text">
          <h1>
            <FiLifeBuoy aria-hidden /> Support desk
          </h1>
          <p className="page-subtitle">
            Freshdesk-style tickets for <strong>{supportInbox}</strong>. New email creates a
            ticket; public comments email the customer.
          </p>
        </div>
        <div className="support-header-actions">
          {canCreate && (
            <Link to="/support/new" className="btn btn-primary">
              <FiPlus aria-hidden /> Log new ticket
            </Link>
          )}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              refetchList();
              if (selectedId) refetchDetail();
            }}
            disabled={listFetching}
          >
            <FiRefreshCw aria-hidden /> Refresh
          </button>
        </div>
      </FadeIn>

      <div className="support-stats" role="group" aria-label="Ticket counts">
        <div className="support-stat">
          <span className="support-stat-value">{statsData?.open_total ?? '—'}</span>
          <span className="support-stat-label">Active</span>
        </div>
        <div className="support-stat">
          <span className="support-stat-value">{statsData?.by_status?.open ?? '—'}</span>
          <span className="support-stat-label">Open</span>
        </div>
        <div className="support-stat">
          <span className="support-stat-value">{statsData?.by_status?.pending ?? '—'}</span>
          <span className="support-stat-label">Pending</span>
        </div>
        <div className="support-stat">
          <span className="support-stat-value">{statsData?.unassigned ?? '—'}</span>
          <span className="support-stat-label">Unassigned</span>
        </div>
      </div>

      <div
        className={`support-workspace${selectedId ? ' support-workspace--split' : ''}${
          selectedId ? ' support-workspace--desk' : ''
        }`}
      >
        <section className="support-queue" aria-label="Ticket queue">
          <div className="support-toolbar">
            <ListSearchBar
              value={search}
              onChange={setSearch}
              placeholder="Search tickets, email, subject…"
            />
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label="Filter by status"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value || 'all'} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              aria-label="Filter by priority"
            >
              {PRIORITY_OPTIONS.map((o) => (
                <option key={o.value || 'any'} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {listLoading ? (
            <p className="support-empty">Loading tickets…</p>
          ) : tickets.length === 0 ? (
            <div className="support-empty">
              <FiMail aria-hidden />
              <p>No tickets yet.</p>
              <p className="support-empty-hint">
                New tickets appear when customers email support@gobunnyy.com.
              </p>
            </div>
          ) : (
            <ul className="support-ticket-list">
              {tickets.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    className={`support-ticket-row support-ticket-row--status-${t.status || 'open'} support-ticket-row--priority-${t.priority || 'normal'}${
                      t.sla?.overdue ? ' is-overdue-sla' : ''
                    }${
                      String(selectedId) === String(t.id) ? ' is-selected' : ''
                    }`}
                    onClick={() => selectTicket(t)}
                  >
                    <div className="support-ticket-row-top">
                      <span className="support-ticket-number">{t.ticket_number}</span>
                      <div className="support-ticket-row-badges">
                        {t.sla?.overdue ? (
                          <span className="support-overdue-mark" title="SLA missed">
                            Overdue
                          </span>
                        ) : null}
                        <StatusBadge status={t.status} />
                      </div>
                    </div>
                    <div className="support-ticket-subject">{t.subject}</div>
                    <div className="support-ticket-meta">
                      <span>{t.requester_name || t.requester_email}</span>
                      <span>{formatWhen(t.last_message_at || t.createdon_timestamp)}</span>
                    </div>
                    <div className="support-ticket-meta support-ticket-meta--sla">
                      <PriorityBadge priority={t.priority} />
                      {t.sla?.enabled ? <SupportSlaClock sla={t.sla} size="sm" /> : null}
                      <span>
                        {t.message_count || 0} comment{(t.message_count || 0) === 1 ? '' : 's'}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <ListPager
            page={listData?.pagination?.page || page}
            pageCount={listData?.pagination?.pages || 1}
            total={listData?.pagination?.total}
            limit={listData?.pagination?.limit || DEFAULT_LIST_LIMIT}
            onPageChange={setPage}
            disabled={listLoading || listFetching}
          />
        </section>

        <section className="support-detail" aria-label="Ticket workspace">
          {!selectedId ? (
            <div className="support-empty support-empty--detail">
              <FiLifeBuoy aria-hidden />
              <p>Select a ticket to open the workspace.</p>
            </div>
          ) : detailLoading && !ticket ? (
            <p className="support-empty">Loading ticket…</p>
          ) : !ticket ? (
            <p className="support-empty" role="alert">
              Ticket not found.
            </p>
          ) : (
            <div className="support-desk">
              <div className="support-desk-main">
                {ticket.merged_into_id && (
                  <div className="support-banner support-banner--warn" role="status">
                    <FiAlertCircle aria-hidden />
                    <span>
                      This ticket was merged into{' '}
                      <button
                        type="button"
                        className="support-link-btn"
                        onClick={() =>
                          selectTicket({
                            id: ticket.merged_into_id,
                            ticket_number: ticket.merged_into_number,
                          })
                        }
                      >
                        {ticket.merged_into_number || `ticket #${ticket.merged_into_id}`}
                      </button>
                      . Add comments on the primary ticket.
                    </span>
                  </div>
                )}

                <header className="support-detail-header">
                  <div className="support-detail-heading">
                    <div className="support-detail-kicker">
                      <span className="support-ticket-number">{ticket.ticket_number}</span>
                      <StatusBadge status={ticket.status} />
                      <PriorityBadge priority={ticket.priority} />
                      {ticket.sla?.overdue ? (
                        <span className="support-overdue-mark" title="SLA target missed">
                          Overdue
                        </span>
                      ) : null}
                      {ticket.sla?.enabled ? (
                        <SupportSlaClock sla={ticket.sla} size="lg" />
                      ) : null}
                    </div>
                    <h2>{ticket.subject}</h2>
                    <p className="support-detail-from">
                      <FiUser aria-hidden /> {ticket.requester_name || 'Customer'}{' '}
                      &lt;{ticket.requester_email}&gt;
                    </p>
                  </div>
                  {canManage && !ticket.merged_into_id && (
                    <div className="support-detail-actions">
                      <button type="button" className="btn btn-secondary" onClick={openMerge}>
                        <FiGitMerge aria-hidden /> Merge duplicate
                      </button>
                    </div>
                  )}
                </header>

                <div className="support-comments" role="log" aria-live="polite" aria-label="Comments">
                  <div className="support-comments-head">
                    <div>
                      <h3>Conversation</h3>
                      <span className="support-comments-count">
                        {messages.length} message{messages.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <ul className="support-thread-legend" aria-label="Message color key">
                      <li>
                        <span className="support-legend-swatch support-legend-swatch--in" />
                        Incoming
                      </li>
                      <li>
                        <span className="support-legend-swatch support-legend-swatch--out" />
                        Outgoing
                      </li>
                      <li>
                        <span className="support-legend-swatch support-legend-swatch--note" />
                        Private note
                      </li>
                    </ul>
                  </div>
                  {messages.length === 0 ? (
                    <p className="support-empty-hint">No comments yet.</p>
                  ) : (
                    <ol className="support-comment-list">
                      {messages.map((m) => {
                        const kind = commentKind(m);
                        const author =
                          kind === 'note'
                            ? m.author_name || 'Agent'
                            : kind === 'reply'
                              ? m.author_name || 'Support'
                              : m.author_name || m.author_email || 'Customer';
                        return (
                          <li
                            key={m.id}
                            className={`support-comment support-comment--${kind}`}
                          >
                            <article className="support-comment-card">
                              <header className="support-comment-header">
                                <div className="support-comment-identity">
                                  <div className="support-comment-author-row">
                                    <strong>{author}</strong>
                                    <DirectionChip kind={kind} />
                                  </div>
                                  <span className="support-comment-kind">
                                    {kind === 'note' ? (
                                      <FiLock aria-hidden />
                                    ) : kind === 'reply' ? (
                                      <FiArrowUpRight aria-hidden />
                                    ) : (
                                      <FiArrowDownLeft aria-hidden />
                                    )}{' '}
                                    {commentLabel(m)}
                                  </span>
                                </div>
                                <time dateTime={m.createdon_timestamp}>
                                  <FiClock aria-hidden /> {formatWhen(m.createdon_timestamp)}
                                </time>
                              </header>
                              <pre className="support-comment-body">{m.body_text}</pre>
                              {m.attachments?.length ? (
                                <ul className="support-comment-files">
                                  {m.attachments.map((file) => (
                                    <li key={file.id}>
                                      <button
                                        type="button"
                                        className="support-file-link"
                                        onClick={() => downloadAttachment(m.id, file)}
                                      >
                                        <FiPaperclip aria-hidden /> {file.file_name}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              ) : null}
                            </article>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                  <div ref={threadEndRef} />
                </div>

                {canManage && !ticket.merged_into_id ? (
                  <div
                    className={`support-composer${
                      noteMode ? ' support-composer--note' : ' support-composer--reply'
                    }`}
                  >
                    <div className="support-composer-tabs" role="tablist">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={!noteMode}
                        className={`support-composer-tab support-composer-tab--reply${!noteMode ? ' is-active' : ''}`}
                        onClick={() => setNoteMode(false)}
                      >
                        <FiMail aria-hidden /> Public reply
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={noteMode}
                        className={`support-composer-tab support-composer-tab--note${noteMode ? ' is-active' : ''}`}
                        onClick={() => setNoteMode(true)}
                      >
                        <FiLock aria-hidden /> Private note
                      </button>
                    </div>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={5}
                      className={noteMode ? 'support-composer-input--note' : 'support-composer-input--reply'}
                      placeholder={
                        noteMode
                          ? 'Private note for the team — not emailed…'
                          : 'Public reply — emailed to the customer as support@gobunnyy.com…'
                      }
                      disabled={sendMutation.isLoading}
                    />
                    {replyFiles.length ? (
                      <ul className="support-composer-files">
                        {replyFiles.map((file, index) => (
                          <li key={`${file.name}-${index}`}>
                            <FiPaperclip aria-hidden />
                            <span>{file.name}</span>
                            <button
                              type="button"
                              aria-label={`Remove ${file.name}`}
                              onClick={() =>
                                setReplyFiles((current) => current.filter((_, item) => item !== index))
                              }
                            >
                              <FiX aria-hidden />
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <div className="support-composer-footer">
                      <p className="support-composer-hint">
                        {noteMode
                          ? 'Amber notes stay in this app only — attached files are saved on the note and never emailed.'
                          : `Teal outgoing reply emails ${ticket.requester_email} · files are attached to that email`}
                      </p>
                      <div className="support-composer-actions">
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          className="support-file-input"
                          onChange={(e) => {
                            addReplyFiles(Array.from(e.target.files || []));
                            e.target.value = '';
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-secondary support-attach-btn"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={sendMutation.isLoading || replyFiles.length >= 5}
                        >
                          <FiPaperclip aria-hidden /> Attach
                        </button>
                      <button
                        type="button"
                        className={`btn ${noteMode ? 'btn-secondary support-btn-note' : 'btn-primary support-btn-reply'}`}
                        disabled={(!replyText.trim() && replyFiles.length === 0) || sendMutation.isLoading}
                        onClick={() => sendMutation.mutate()}
                      >
                        <FiSend aria-hidden />{' '}
                        {noteMode ? 'Add note' : 'Add reply & email'}
                      </button>
                      </div>
                    </div>
                  </div>
                ) : !canManage ? (
                  <p className="support-readonly-hint">
                    You can view tickets. Ask an admin for <code>support_manage</code> to comment.
                  </p>
                ) : null}
              </div>

              <aside className="support-props" aria-label="Ticket properties">
                <h3>Properties</h3>
                {canManage && !ticket.merged_into_id ? (
                  <div className="support-props-fields">
                    <label className={`support-prop-label support-prop-label--status-${ticket.status}`}>
                      Status
                      <select
                        className={`support-select-status support-select-status--${ticket.status}`}
                        value={ticket.status}
                        onChange={(e) => patchMutation.mutate({ status: e.target.value })}
                        disabled={patchMutation.isLoading}
                      >
                        <option value="open">Open</option>
                        <option value="pending">Pending</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </label>
                    <label className={`support-prop-label support-prop-label--priority-${ticket.priority || 'normal'}`}>
                      Priority
                      <select
                        className={`support-select-priority support-select-priority--${ticket.priority || 'normal'}`}
                        value={ticket.priority}
                        onChange={(e) => patchMutation.mutate({ priority: e.target.value })}
                        disabled={patchMutation.isLoading}
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </label>
                    <label>
                      Assignee
                      <select
                        value={ticket.assignee_id || ''}
                        onChange={(e) =>
                          patchMutation.mutate({
                            assignee_id: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                        disabled={patchMutation.isLoading}
                      >
                        <option value="">Unassigned</option>
                        {(agentsData || []).map((a) => (
                          <option key={a.id} value={a.id}>
                            {[a.first_name, a.last_name].filter(Boolean).join(' ') || a.email}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                ) : (
                  <dl className="support-props-readonly">
                    <div>
                      <dt>Status</dt>
                      <dd>
                        <StatusBadge status={ticket.status} />
                      </dd>
                    </div>
                    <div>
                      <dt>Priority</dt>
                      <dd>
                        <PriorityBadge priority={ticket.priority} />
                      </dd>
                    </div>
                    <div>
                      <dt>Assignee</dt>
                      <dd>{ticket.assignee_name || 'Unassigned'}</dd>
                    </div>
                  </dl>
                )}

                <div className="support-props-section">
                  <h4>
                    <FiTag aria-hidden /> Tags
                  </h4>
                  <div className="support-tags">
                    {(ticket.tags || []).length === 0 && (
                      <span className="support-muted">No tags</span>
                    )}
                    {(ticket.tags || []).map((tag) => (
                      <span key={tag} className="support-tag">
                        {tag}
                        {canManage && !ticket.merged_into_id && (
                          <button
                            type="button"
                            aria-label={`Remove tag ${tag}`}
                            onClick={() => removeTag(tag)}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                  {canManage && !ticket.merged_into_id && (
                    <div className="support-tag-add">
                      <input
                        value={tagDraft}
                        onChange={(e) => setTagDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addTag();
                          }
                        }}
                        placeholder="Add tag…"
                        aria-label="Add tag"
                      />
                      <button type="button" className="btn btn-secondary btn-sm" onClick={addTag}>
                        Add
                      </button>
                    </div>
                  )}
                </div>

                <div className="support-props-section">
                  <h4>Requester</h4>
                  <p className="support-props-value">{ticket.requester_name || '—'}</p>
                  <a className="support-props-email" href={`mailto:${ticket.requester_email}`}>
                    {ticket.requester_email}
                  </a>
                  <p className="support-muted">
                    Created {formatWhen(ticket.createdon_timestamp)}
                  </p>
                </div>

                <div className="support-props-section">
                  <h4>Related tickets</h4>
                  {related.length === 0 ? (
                    <p className="support-muted">No other tickets from this requester.</p>
                  ) : (
                    <ul className="support-related">
                      {related.map((r) => (
                        <li key={r.id}>
                          <button type="button" onClick={() => selectTicket(r)}>
                            <span className="support-ticket-number">{r.ticket_number}</span>
                            <span className="support-related-subject">{r.subject}</span>
                            <StatusBadge status={r.status} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {redirectTo && (
                  <div className="support-props-section">
                    <h4>Primary ticket</h4>
                    <button
                      type="button"
                      className="support-primary-link"
                      onClick={() => selectTicket(redirectTo)}
                    >
                      Open {redirectTo.ticket_number} <FiExternalLink aria-hidden />
                    </button>
                  </div>
                )}
              </aside>
            </div>
          )}
        </section>
      </div>

      <Modal
        open={mergeOpen}
        onClose={() => {
          setMergeOpen(false);
          setMergeSourceId(null);
          setMergeNote('');
        }}
        ariaLabel="Merge duplicate ticket"
        contentClassName="support-merge-modal"
      >
        <div className="support-merge">
          <header>
            <h2>
              <FiGitMerge aria-hidden /> Merge into {ticket?.ticket_number}
            </h2>
            <p>
              Choose a duplicate. Its comments move here; it closes as merged. Future emails to
              either ticket number land on this ticket.
            </p>
          </header>
          <ListSearchBar
            value={mergeSearch}
            onChange={setMergeSearch}
            placeholder="Search by ticket #, subject, or email…"
          />
          <div className="support-merge-list" role="listbox" aria-label="Merge candidates">
            {mergeCandidatesLoading ? (
              <p className="support-muted">Loading…</p>
            ) : (mergeCandidatesData || []).length === 0 ? (
              <p className="support-muted">No candidates. Try a search.</p>
            ) : (
              (mergeCandidatesData || []).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="option"
                  aria-selected={mergeSourceId === c.id}
                  className={`support-merge-row${mergeSourceId === c.id ? ' is-selected' : ''}`}
                  onClick={() => setMergeSourceId(c.id)}
                >
                  <div className="support-ticket-row-top">
                    <span className="support-ticket-number">{c.ticket_number}</span>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="support-ticket-subject">{c.subject}</div>
                  <div className="support-ticket-meta">
                    <span>{c.requester_email}</span>
                    <span>{formatWhen(c.last_message_at || c.createdon_timestamp)}</span>
                  </div>
                </button>
              ))
            )}
          </div>
          <label className="support-merge-note">
            Merge note (optional)
            <textarea
              rows={2}
              value={mergeNote}
              onChange={(e) => setMergeNote(e.target.value)}
              placeholder="Why these are duplicates…"
            />
          </label>
          <div className="support-merge-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setMergeOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!mergeSourceId || mergeMutation.isLoading}
              onClick={() => mergeMutation.mutate()}
            >
              <FiGitMerge aria-hidden /> Merge tickets
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Support;
